import { AppThunkDispatch } from '@common/types/common-root-state-type';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { namespace } from '@common-config/constants';
import { appConfig } from '@/common/config/app.config';
import { backendApi, type ConnectMcpServerResult, type McpPreset, type MyBridgeMcpServer } from '@common/reducers/backend-api/backend-api';
import { AlertTriangle, BadgeCheck, Check, Clock, Link2, Loader2, Plug, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { watchSignIn } from './mcp-sign-in-watch';
import { isExtensionContext } from '@common/services/detect-context';
import { DeleteConfirmationDialog } from '@common/components/delete-confirmation-dialog';
import { useConfirmDialog } from '@common/components/use-confirm-dialog';
import { ToolPolicyControls } from './McpToolPolicy';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The agent's EXTERNAL MCP servers (backend agent-mcp-servers,
 * docs/claude-bridge/agent-external-mcp-plan.md). Connect from the library (a preset)
 * or any MCP URL. An OAuth server signs in through a popup: the backend callback page
 * hands `{state, code}` back to THIS window, and this window's authenticated request
 * finishes the connection — so a sign-in link opened anywhere else attaches nothing.
 * EVERY TOOL STARTS OFF — only ticked tools can be called. The credential is
 * write-only. While the backend kill switch is off (the library answers 403) only
 * already-connected servers are listed, with Remove — a credential can always be deleted.
 *
 * Three surfaces, one implementation:
 *  - the agent form (`McpServersSection`): every server + the library;
 *  - a NEW agent's form: the library only — the picks are attached unconnected once it is created;
 *  - the builder chat (`McpConnectCard`): only the servers that still need the user
 *    (not connected / no tool ticked), e.g. the ones the builder added from the prompt.
 *
 * Instead of signing in themselves, the owner can copy a sign-in link (`CopyLinkButton`): whoever
 * opens it goes straight to the service's sign-in, without a Blueticks account, and the connection lands here.
 *
 * Tool descriptions are third-party text: rendered as plain text only.
 */
/** The full integrations directory page (MyBridgePage routes the segment). */
export const MCP_DIRECTORY_SEGMENT = 'integrations';
export const MCP_DIRECTORY_PATH = `/agents/${MCP_DIRECTORY_SEGMENT}`;

type Classes = { section: string; title: string; label: string; hint: string; input: string };

export interface McpServersSectionProps {
  /** Absent for an unsaved agent: the section then only collects `picks`. */
  agentId?: string;
  /** The agent answers anyone (block-list scope) → whoever writes to it can use these tools. */
  publicAgent: boolean;
  classes: Classes;
  /** New agent only: the integrations chosen before it exists (library ids / MCP URLs). */
  picks?: string[];
  onPicksChange?: (picks: string[]) => void;
}

/** What the backend callback page posts to this window. */
const MESSAGE_TYPE = 'blueticks_agent_mcp';

/**
 * The ONE sign-in in progress on this page (all surfaces share one popup window). The hook
 * instance that started it (`owner`) completes it — for the agent it was started for, even if
 * that surface has since switched agents — so two surfaces never both complete it. If the
 * owner unmounts first (e.g. the builder chat's card during a new turn), it releases the sign-in
 * (`owner: null`) and whichever surface sees the callback completes it. A sign-in abandoned in
 * one surface is simply replaced by the next one started anywhere.
 */
let pendingSignIn: { owner: object | null; agentId: string; serverId: string } | null = null;

const errorText = (e: unknown, fallback: string): string => (e as { data?: { message?: string } })?.data?.message || fallback;

/** Open the popup NOW (inside the click) so the browser allows it; it is pointed at the sign-in URL once known. */
function openBlankPopup(): Window | null {
  const w = 520;
  const h = 680;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  return window.open('about:blank', 'blueticks-mcp-connect', `width=${w},height=${h},left=${left},top=${top}`);
}

/** Integrations switched on? `false` = the kill switch is off (the library answers 403); `undefined` = still loading. Shares the picker's first page in the cache. */
function useMcpEnabled(): boolean | undefined {
  // Same cache entry as the library's first view ({q:'', category:''}) — no extra request.
  const { data, error } = backendApi.useGetMcpPresetsQuery({ q: '', category: '' });
  if ((error as { status?: number } | undefined)?.status === 403) return false;
  return data ? true : undefined;
}

/**
 * Connect / reconnect with the popup sign-in, shared by every surface. `run` opens the
 * popup inside the click when a sign-in may be needed; the callback page's message
 * comes back here and this window's authenticated PATCH finishes the connection.
 */
/** The user declined the browser permission the extension needs to finish a sign-in (#1179). */
class PermissionDeclinedError extends Error {}

export function useMcpConnect(agentId: string, onConnected?: (agentId: string) => void) {
  const { t } = useTranslation(namespace);
  const [patch] = backendApi.usePatchMyBridgeAgentMcpServerMutation();
  const [connectNew] = backendApi.useConnectMyBridgeAgentMcpServerMutation();
  const [bind] = backendApi.useBindMyBridgeAgentMcpSignInMutation();
  const dispatch = useDispatch<AppThunkDispatch>();
  const watch = useRef<AbortController | null>(null);
  /** The extension binds from its background worker (a content script's fetch is cross-site); the web app calls the API directly. */
  const bindBrowser = async (agent: string, id: string): Promise<void> => {
    if (!isExtensionContext()) {
      await bind({ agentId: agent, id }).unwrap();
      return;
    }
    const { ContentMessageServiceActions } = await import('../../extension/context/content/services/contentMessageServiceActions');
    const { status, error } = await ContentMessageServiceActions.bindMcpSignIn(agent, id);
    if (error === 'permission_declined') throw new PermissionDeclinedError();
    if (status < 200 || status >= 300) throw new Error(`bind failed (${status})`);
  };
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const owner = useRef({}).current;

  useEffect(() => {
    const apiOrigin = new URL(appConfig.API_ENDPOINT).origin;
    const onMessage = async (event: MessageEvent) => {
      if (event.origin !== apiOrigin || event.data?.type !== MESSAGE_TYPE || !pendingSignIn || (pendingSignIn.owner !== owner && pendingSignIn.owner !== null)) return;
      const { agentId: signedInAgent, serverId } = pendingSignIn;
      pendingSignIn = null;
      const { code, state } = event.data as { code?: string; state?: string };
      if (!code || !state) {
        setError(t('myBridge.mcp.signInCancelled'));
        return;
      }
      try {
        await patch({ agentId: signedInAgent, id: serverId, code, state }).unwrap();
        setError(null);
        onConnected?.(signedInAgent);
      } catch (e) {
        setError(errorText(e, t('myBridge.mcp.connectFailed')));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [owner, patch, t, onConnected]);
  useEffect(() => () => watch.current?.abort(), []);
  // On unmount only (`owner` never changes): hand an unfinished sign-in to whichever surface remains.
  useEffect(
    () => () => {
      if (pendingSignIn?.owner === owner) pendingSignIn = { ...pendingSignIn, owner: null };
    },
    [owner],
  );

  const run = async (mayNeedSignIn: boolean, call: () => Promise<ConnectMcpServerResult>): Promise<boolean> => {
    setError(null);
    setBusy(true);
    const popup = mayNeedSignIn ? openBlankPopup() : null;
    try {
      const res = await call();
      if (!res.authorizationUrl) {
        popup?.close();
        onConnected?.(agentId);
        return true;
      }
      if (!popup) {
        setError(t('myBridge.mcp.popupBlocked'));
        return false;
      }
      pendingSignIn = { owner, agentId, serverId: res.server.id };
      // #1179: bind this browser first (cookie), so the callback can finish the sign-in even when the provider
      // severs the popup's opener. Best effort: without it the opener path below still works for most providers.
      try {
        await bindBrowser(agentId, res.server.id);
      } catch (e) {
        // Without the binding the callback cannot finish this sign-in itself: say so, and do not open the provider.
        pendingSignIn = null;
        popup.close();
        setError(e instanceof PermissionDeclinedError ? t('myBridge.mcp.permissionDeclined') : errorText(e, t('myBridge.mcp.connectFailed')));
        return false;
      }
      popup.location.href = res.authorizationUrl;
      watch.current?.abort();
      const ctl = new AbortController();
      watch.current = ctl;
      void watchSignIn({
        serverId: res.server.id,
        signal: ctl.signal,
        fetchRows: () => dispatch(backendApi.endpoints.getMyBridgeAgentMcpServers.initiate({ agentId }, { forceRefetch: true })).unwrap(),
      }).then((outcome) => {
        if (outcome.kind === 'connected') {
          if (pendingSignIn?.serverId === res.server.id) pendingSignIn = null;
          setError(null);
          onConnected?.(agentId);
        } else if (outcome.kind === 'failed') {
          if (pendingSignIn?.serverId === res.server.id) pendingSignIn = null;
          setError(outcome.reason);
        } else if (outcome.kind === 'timeout') {
          setError(t('myBridge.mcp.signInTimedOut'));
        }
      });
      return true;
    } catch (e) {
      popup?.close();
      setError(errorText(e, t('myBridge.mcp.connectFailed')));
      return false;
    } finally {
      setBusy(false);
    }
  };

  /** Connect a never-connected server, or reconnect one (keeps its ticked tools) — a key server with the new key. */
  const connectServer = (s: MyBridgeMcpServer, apiKey?: string) =>
    run(!apiKey, () => patch({ agentId, id: s.id, reconnect: true, ...(apiKey ? { apiKey } : {}) }).unwrap() as Promise<ConnectMcpServerResult>);

  /** Add a library entry or URL to the agent. Without a key the server may still ask for a sign-in, so the popup opens (and closes if it doesn't). */
  const addServer = (server: string, apiKey?: string) => run(!apiKey, () => connectNew({ agentId, server, ...(apiKey ? { apiKey } : {}) }).unwrap());

  /** Any other server action (tools, refresh, remove) with the same busy / error handling. */
  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(errorText(e, t('myBridge.mcp.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return { addServer, connectServer, act, error, setError, busy };
}

/** `value`, once it has stopped changing for `ms` (a search box). */
export function useDebouncedValue(value: string, ms = 250): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value.trim()), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/** A service logo (third-party image URL); a plug when there is none or it fails to load. */
export const McpLogo: React.FC<{ src?: string; size?: number }> = ({ src, size = 40 }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className="flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-ai-border bg-ai-surface" style={{ width: size, height: size }}>
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} className="object-contain" style={{ width: size * 0.62, height: size * 0.62 }} />
      ) : (
        <Plug className="text-ai-text-secondary" style={{ width: size * 0.45, height: size * 0.45 }} />
      )}
    </span>
  );
};

/** A category's display name (the directory's taxonomy id → translated label, or the id humanised). */
export const useCategoryLabel = () => {
  const { t } = useTranslation(namespace);
  return (id: string) => t(`myBridge.mcp.categories.${id}`, id.replace(/-/g, ' & ').replace(/^./, (c) => c.toUpperCase()));
};

/** The category filter row: "All" + every category (with its count). `value` '' = all. */
export const CategoryChips: React.FC<{ value: string; onChange: (category: string) => void }> = ({ value, onChange }) => {
  const { t } = useTranslation(namespace);
  const label = useCategoryLabel();
  const { data: categories = [] } = backendApi.useGetMcpCategoriesQuery();
  const chip = (id: string, text: string, count?: number) => (
    <button
      key={id || 'all'}
      type="button"
      onClick={() => onChange(id)}
      className={`shrink-0 rounded-ai-pill px-3 py-1 text-xs transition-colors ${value === id ? 'bg-ai-text text-ai-surface' : 'bg-ai-surface text-ai-text hover:bg-ai-border'}`}
      data-testid={`agent-mcp-category-${id || 'all'}`}
    >
      {text}
      {count !== undefined && <span className="ms-1 opacity-60">{count}</span>}
    </button>
  );
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="agent-mcp-categories">
      {chip('', t('myBridge.mcp.allCategories'))}
      {categories.map((c) => chip(c.id, label(c.id), c.count))}
    </div>
  );
};

/** The card grid (the picker and the directory page). */
export const PRESET_GRID = 'grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(260px,1fr))]';

/**
 * One library entry as a directory card: logo, name (+ verified), summary, publisher, + / ✓ — or "coming soon",
 * which can't be picked (with `opensDetails` it still opens its details, where adding is decided).
 */
export const PresetCard: React.FC<{ preset: McpPreset; onPick: () => void; added?: boolean; busy?: boolean; opensDetails?: boolean }> = ({
  preset: p,
  onPick,
  added = false,
  busy = false,
  opensDetails = false,
}) => {
  const { t } = useTranslation(namespace);
  const soon = p.available === false;
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={busy || added || (soon && !opensDetails)}
      aria-description={soon ? t('myBridge.mcp.comingSoonHint') : undefined}
      className={`flex items-start gap-3 rounded-ai-md border border-ai-border bg-ai-surface p-3 text-start transition-colors hover:border-ai-text-secondary disabled:cursor-default disabled:hover:border-ai-border ${soon ? 'opacity-75' : ''}`}
      data-testid={`agent-mcp-preset-${p.id}`}
    >
      <McpLogo src={p.icon} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1 text-sm font-medium text-ai-text">
          <span className="truncate">{p.name}</span>
          {p.verified && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-ai-text-secondary" aria-label={t('myBridge.mcp.verified')} />}
        </span>
        {(p.summary || p.description) && <span className="line-clamp-2 block text-xs text-ai-text-secondary">{p.summary || p.description}</span>}
        {p.by && <span className="mt-0.5 block truncate text-[11px] text-ai-text-secondary opacity-80">{t('myBridge.mcp.by', { by: p.by })}</span>}
      </span>
      {soon ? (
        <span className="flex shrink-0 items-center gap-1 rounded-md bg-ai-muted px-2 py-1 text-[11px] text-ai-text-secondary" data-testid="agent-mcp-coming-soon">
          <Clock className="h-3 w-3" />
          {t('myBridge.mcp.comingSoon')}
        </span>
      ) : (
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${added ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-ai-muted text-ai-text'}`}
          role={added ? 'img' : undefined}
          aria-label={added ? t('myBridge.mcp.added') : undefined}
        >
          {added ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      )}
    </button>
  );
};

/** Search + category chips over the library (the picker and the directory page). */
export const PresetSearch: React.FC<{
  text: string;
  onText: (text: string) => void;
  category: string;
  onCategory: (category: string) => void;
  loading?: boolean;
  inputClassName?: string;
  testId?: string;
}> = ({ text, onText, category, onCategory, loading, inputClassName = '', testId = 'agent-mcp-search' }) => {
  const { t } = useTranslation(namespace);
  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ai-text-secondary" />
        <Input value={text} onChange={(e) => onText(e.target.value)} placeholder={t('myBridge.mcp.search')} className={`${inputClassName} ps-9`} data-testid={testId} />
        {loading && <Loader2 className="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-ai-text-secondary" />}
      </div>
      <CategoryChips value={category} onChange={onCategory} />
    </div>
  );
};

/** THE key entry (adding a key integration, or reconnecting one): the key, where to get it, and the action. */
export const KeyField: React.FC<{
  name: string;
  helpUrl?: string;
  busy: boolean;
  submitLabel: string;
  onSubmit: (key: string) => void;
  onCancel?: () => void;
  inputClassName?: string;
}> = ({ name, helpUrl, busy, submitLabel, onSubmit, onCancel, inputClassName }) => {
  const { t } = useTranslation(namespace);
  const [key, setKey] = useState('');
  return (
    <div className="space-y-2">
      <Input
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder={t('myBridge.mcp.keyFor', { name })}
        type="password"
        autoComplete="off"
        dir="ltr"
        className={inputClassName}
        data-testid="agent-mcp-key"
      />
      {helpUrl && (
        <a href={helpUrl} target="_blank" rel="noopener noreferrer" className="block text-xs underline">
          {t('myBridge.mcp.whereKey')}
        </a>
      )}
      <div className="flex gap-2">
        <Button type="button" size="sm" onClick={() => onSubmit(key.trim())} disabled={!key.trim() || busy} data-testid="agent-mcp-key-submit">
          {busy && <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />}
          {submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            {t('myBridge.mcp.back')}
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * The library as a directory: search + categories (the featured and popular services when
 * neither is set, else the whole catalog), logo cards with + / ✓ / coming soon, any server by
 * URL (+ optional key), and a way out to the full directory page. Shared by the new-agent and
 * the existing-agent flows.
 */
const LibraryPicker: React.FC<{
  classes: Classes;
  busy: boolean;
  /** `apiKey` only for an existing agent (a new agent's picks are connected after it is created). */
  withKeys: boolean;
  /** Already on this agent (✓). */
  isAdded: (p: McpPreset) => boolean;
  /** `server` = a library id or an MCP URL — the one shape for "which server" everywhere. */
  onPick: (server: string, apiKey?: string, preset?: McpPreset) => void;
  onCancel: () => void;
  /** The full directory page (with this agent preselected). */
  directoryHref: string;
}> = ({ classes, busy, withKeys, isAdded, onPick, onCancel, directoryHref }) => {
  const { t } = useTranslation(namespace);
  const [keyFor, setKeyFor] = useState<McpPreset | null>(null);
  const [text, setText] = useState('');
  const q = useDebouncedValue(text);
  const [category, setCategory] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const { data: results = [], isFetching } = backendApi.useGetMcpPresetsQuery({ q, category });
  // Only a well-formed https URL can be picked (the backend re-validates; this just stops an obvious typo early).
  const urlOk = /^https:\/\/[^\s/]+/i.test(customUrl.trim());

  if (keyFor) {
    return (
      <div className="space-y-2 rounded-ai-md bg-ai-muted px-3 py-3" data-testid="agent-mcp-library">
        <div className="flex items-center gap-2">
          <McpLogo src={keyFor.icon} size={28} />
          <div className={classes.label}>{keyFor.name}</div>
        </div>
        <KeyField
          name={keyFor.name}
          helpUrl={keyFor.keyHelpUrl}
          busy={busy}
          submitLabel={t('myBridge.mcp.connect')}
          onSubmit={(key) => onPick(keyFor.id, key, keyFor)}
          onCancel={() => setKeyFor(null)}
          inputClassName={classes.input}
        />
      </div>
    );
  }
  return (
    <div className="space-y-3 rounded-ai-md bg-ai-muted px-3 py-3" data-testid="agent-mcp-library">
      <PresetSearch text={text} onText={setText} category={category} onCategory={setCategory} loading={isFetching} inputClassName={classes.input} />
      <div className={`${PRESET_GRID} max-h-[440px] overflow-y-auto`}>
        {results.map((p) => (
          <PresetCard key={p.id} preset={p} added={isAdded(p)} busy={busy} onPick={() => (withKeys && p.auth === 'key' ? setKeyFor(p) : onPick(p.id, undefined, p))} />
        ))}
      </div>
      {!isFetching && results.length === 0 && <p className={classes.hint}>{t('myBridge.mcp.noResults')}</p>}
      <Link to={directoryHref} className="inline-block text-sm font-medium text-ai-text underline-offset-4 hover:underline" data-testid="agent-mcp-browse-all">
        {t('myBridge.mcp.browseAll')} →
      </Link>
      <div className="space-y-1.5">
        <div className={classes.label}>{t('myBridge.mcp.customUrl')}</div>
        <Input value={customUrl} onChange={(e) => setCustomUrl(e.target.value)} placeholder={t('myBridge.mcp.customUrlPlaceholder', 'https://…/mcp')} dir="ltr" className={classes.input} data-testid="agent-mcp-url" />
        {withKeys && (
          <Input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={t('myBridge.mcp.optionalKey')} type="password" autoComplete="off" dir="ltr" className={classes.input} />
        )}
        <p className={classes.hint}>{t('myBridge.mcp.customHint')}</p>
      </div>
      <div className="flex gap-2">
        <Button type="button" size="sm" onClick={() => onPick(customUrl.trim(), apiKey.trim() || undefined)} disabled={!urlOk || busy} data-testid="agent-mcp-connect-url">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          <span className={busy ? 'ms-1.5' : ''}>{withKeys ? t('myBridge.mcp.connect') : t('myBridge.mcp.addPick')}</span>
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('myBridge.mcp.cancel')}
        </Button>
      </div>
      <p className={classes.hint}>{t('myBridge.mcp.proServices')}</p>
    </div>
  );
};

/**
 * One click: create a sign-in link for this server and copy it. Whoever opens it goes straight to
 * the service's own sign-in — no Blueticks account — and the connection lands on this agent with
 * every tool off. Each click replaces the last link (that is also how one is withdrawn). Without
 * clipboard access (an insecure page) the link is shown to copy by hand.
 */
const CopyLinkButton: React.FC<{ agentId: string; server: MyBridgeMcpServer; classes: Classes; disabled: boolean }> = ({ agentId, server: s, classes, disabled }) => {
  const { t } = useTranslation(namespace);
  const [patch, { isLoading }] = backendApi.usePatchMyBridgeAgentMcpServerMutation();
  const [result, setResult] = useState<{ copied?: boolean; link?: string; error?: string }>({});
  const { confirm, dialog } = useConfirmDialog();

  const copyLink = async () => {
    // A new link cancels the one the customer may already have.
    if (s.invite && !(await confirm({ content: t('myBridge.mcp.invite.newLinkConfirm'), title: t('confirmDialog.title'), confirmLabel: t('confirmDialog.continue'), destructive: false }))) return;
    setResult({});
    try {
      const { inviteUrl } = (await patch({ agentId, id: s.id, invite: true }).unwrap()) as ConnectMcpServerResult;
      if (!inviteUrl) return; // it needed no sign-in: connected already
      const copied = (await navigator.clipboard?.writeText(inviteUrl).then(() => true, () => false)) ?? false;
      setResult(copied ? { copied } : { link: inviteUrl });
    } catch (e) {
      setResult({ error: errorText(e, t('myBridge.mcp.connectFailed')) });
    }
  };

  return (
    <>
      {dialog}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void copyLink()}
        disabled={disabled || isLoading}
        data-testid="agent-mcp-copy-link"
      >
        {isLoading ? <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" /> : result.copied ? <Check className="me-1 h-3.5 w-3.5" /> : <Link2 className="me-1 h-3.5 w-3.5" />}
        {result.copied ? t('myBridge.mcp.invite.copied') : s.invite ? t('myBridge.mcp.invite.newLink') : t('myBridge.mcp.invite.copy')}
      </Button>
      {(result.copied || result.link) && <p className={`basis-full ${classes.hint}`}>{t('myBridge.mcp.invite.hint', { name: s.title ?? s.name })}</p>}
      {result.link && (
        <Input readOnly value={result.link} dir="ltr" className={`basis-full ${classes.input}`} onFocus={(e) => e.currentTarget.select()} data-testid="agent-mcp-invite-url" />
      )}
      {result.error && <p className="basis-full text-sm text-red-500">{result.error}</p>}
    </>
  );
};

/** One server: its status, connect / reconnect, its tools (all off until ticked). `enabled=false` = switch off: Remove only. */
const ServerCard: React.FC<{
  agentId: string;
  server: MyBridgeMcpServer;
  enabled: boolean;
  classes: Classes;
  connect: ReturnType<typeof useMcpConnect>;
}> = ({ agentId, server: s, enabled, classes, connect }) => {
  const { t, i18n } = useTranslation(namespace);
  const [patch] = backendApi.usePatchMyBridgeAgentMcpServerMutation();
  const [remove] = backendApi.useRemoveMyBridgeAgentMcpServerMutation();
  const [askKey, setAskKey] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const { confirm: confirmReplace, dialog: replaceDialog } = useConfirmDialog();
  // While a link waits for someone else, re-read the list so its connection shows up here (one shared cache entry).
  backendApi.useGetMyBridgeAgentMcpServersQuery({ agentId }, { pollingInterval: s.invite ? 20_000 : 0 });
  const { busy, act } = connect;
  const setTools = (enabledTools: string[]) => act(() => patch({ agentId, id: s.id, enabledTools }).unwrap());
  const onRemove = () => setConfirmRemove(true);
  const onConfirmRemove = () => {
    setConfirmRemove(false);
    void act(() => remove({ agentId, id: s.id }).unwrap());
  };

  return (
    <div className="space-y-2 rounded-ai-md border border-ai-border px-3 py-2" data-testid={`agent-mcp-server-${s.name}`}>
      <DeleteConfirmationDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={onConfirmRemove}
        title={t('myBridge.mcp.removeTitle')}
        content={t('myBridge.mcp.removeConfirm', { name: s.title ?? s.name })}
        confirmLabel={t('myBridge.mcp.remove')}
      />
      {replaceDialog}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <McpLogo src={s.icon} size={32} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-sm font-medium text-ai-text">
              <span className="truncate">{s.title ?? s.name}</span>
              {!s.preset && <span className="rounded bg-ai-muted px-1.5 text-xs text-ai-text-secondary">{t('myBridge.mcp.custom')}</span>}
            </div>
            <div className="truncate text-xs text-ai-text-secondary" dir="ltr">
              {s.url}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {enabled && s.connected && !s.lastError && (
            <Button type="button" variant="ghost" size="sm" onClick={() => act(() => patch({ agentId, id: s.id, refreshTools: true }).unwrap())} disabled={busy} title={t('myBridge.mcp.refreshTools')}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button type="button" variant="ghost" size="sm" onClick={onRemove} disabled={busy} title={t('myBridge.mcp.remove')} data-testid="agent-mcp-remove">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {enabled && (!s.connected || s.lastError) && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
            <span>{s.lastError ?? t('myBridge.mcp.needsConnect')}</span>
            {!askKey && (
              <Button
                type="button"
                variant={s.connected ? 'outline' : 'default'}
                size="sm"
                onClick={async () => {
                  // Connecting yourself replaces the sign-in a waiting link holds.
                  if (s.invite && !(await confirmReplace({ content: t('myBridge.mcp.invite.replaceConfirm'), title: t('confirmDialog.title'), confirmLabel: t('confirmDialog.continue'), destructive: false }))) return;
                  if (s.auth === 'key') setAskKey(true);
                  else void connect.connectServer(s);
                }}
                disabled={busy}
                data-testid="agent-mcp-connect"
              >
                {s.connected ? t('myBridge.mcp.reconnect') : t('myBridge.mcp.connect')}
              </Button>
            )}
            {!askKey && s.auth !== 'key' && <CopyLinkButton agentId={agentId} server={s} classes={classes} disabled={busy} />}
          </div>
          {s.invite && !askKey && (
            <p className={classes.hint} data-testid="agent-mcp-invite-open">
              {t('myBridge.mcp.invite.waiting', { date: new Date(s.invite.expiresAt).toLocaleDateString(i18n.language) })}
            </p>
          )}
          {askKey && (
            <KeyField
              name={s.title ?? s.name}
              busy={busy}
              submitLabel={t('myBridge.mcp.connect')}
              onSubmit={(key) => void connect.connectServer(s, key).then((ok) => ok && setAskKey(false))}
              onCancel={() => setAskKey(false)}
              inputClassName={classes.input}
            />
          )}
        </div>
      )}

      {enabled && s.connected && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className={classes.hint}>
              {s.enabledTools.length === 0 ? t('myBridge.mcp.noToolsEnabled') : t('myBridge.mcp.toolsEnabled', { count: s.enabledTools.length, total: s.tools.length })}
            </span>
            {s.tools.some((x) => x.readOnly && !s.enabledTools.includes(x.name)) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setTools([...new Set([...s.enabledTools, ...s.tools.filter((x) => x.readOnly).map((x) => x.name)])])}
                disabled={busy}
                data-testid="agent-mcp-enable-read"
              >
                {t('myBridge.mcp.enableReadTools')}
              </Button>
            )}
          </div>
          <ul className="max-h-64 space-y-1 overflow-y-auto">
            {s.tools.map((tool) => {
              const id = `mcp-${s.id}-${tool.name}`;
              return (
                <li key={tool.name} className="flex items-start gap-2">
                  <Checkbox
                    id={id}
                    checked={s.enabledTools.includes(tool.name)}
                    disabled={busy}
                    onCheckedChange={(v) => setTools(v === true ? [...s.enabledTools, tool.name] : s.enabledTools.filter((n) => n !== tool.name))}
                    className="mt-0.5"
                  />
                  <div className="min-w-0">
                    <label htmlFor={id} className="min-w-0 cursor-pointer text-sm">
                      <span className="font-mono text-xs" dir="ltr">
                        {tool.name}
                      </span>
                      {tool.readOnly && <span className="ms-1.5 rounded bg-ai-muted px-1 text-[10px] text-ai-text-secondary">{t('myBridge.mcp.readOnly')}</span>}
                      {tool.destructive && <span className="ms-1.5 rounded bg-red-100 px-1 text-[10px] text-red-700 dark:bg-red-950 dark:text-red-300">{t('myBridge.mcp.destructive')}</span>}
                      {tool.description && <span className={`block line-clamp-2 ${classes.hint}`}>{tool.description}</span>}
                    </label>
                    {s.policyAvailable && (
                      <ToolPolicyControls
                        serverId={s.id}
                        toolName={tool.name}
                        enabledTool={s.enabledTools.includes(tool.name)}
                        policy={s.toolPolicy}
                        available={s.policyAvailable}
                        busy={busy}
                        classes={classes}
                        onChange={(toolPolicy) => act(() => patch({ agentId, id: s.id, toolPolicy }).unwrap())}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
};

const Frame: React.FC<{ classes: Classes; children: React.ReactNode }> = ({ classes, children }) => {
  const { t } = useTranslation(namespace);
  return (
    <div className={classes.section}>
      <div className={classes.title}>{t('myBridge.mcp.sectionTitle')}</div>
      {children}
    </div>
  );
};

/** The agent form's section. */
export const McpServersSection: React.FC<McpServersSectionProps> = ({ agentId, publicAgent, classes, picks = [], onPicksChange }) => {
  const enabled = useMcpEnabled();
  if (enabled === undefined) return null;
  if (!agentId) return enabled ? <NewAgentPicks classes={classes} picks={picks} onPicksChange={onPicksChange} /> : null;
  return <AgentServers agentId={agentId} enabled={enabled} publicAgent={publicAgent} classes={classes} />;
};

/** A NEW agent: choose integrations now; they are attached (unconnected) when it is created, then connected with one click. */
const NewAgentPicks: React.FC<{ classes: Classes; picks: string[]; onPicksChange?: (p: string[]) => void }> = ({ classes, picks, onPicksChange }) => {
  const { t } = useTranslation(namespace);
  const [adding, setAdding] = useState(false);
  // The library entries picked here (a URL pick has none) — for their names and logos.
  const [entries, setEntries] = useState<Record<string, McpPreset>>({});
  return (
    <Frame classes={classes}>
      <div className="space-y-3" data-testid="agent-mcp-picks">
        <p className={classes.hint}>{t('myBridge.mcp.newAgentHint')}</p>
        {picks.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {picks.map((p) => (
              <span key={p} className="inline-flex items-center gap-1.5 rounded-ai-pill bg-ai-muted py-1 pe-2.5 ps-1 text-sm" dir="auto">
                <McpLogo src={entries[p]?.icon} size={22} /> {entries[p]?.name ?? p}
                <button type="button" onClick={() => onPicksChange?.(picks.filter((x) => x !== p))} aria-label={t('myBridge.mcp.remove')}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
        {adding ? (
          <LibraryPicker
            classes={classes}
            busy={false}
            withKeys={false}
            isAdded={(p) => picks.includes(p.id)}
            directoryHref={MCP_DIRECTORY_PATH}
            onPick={(server, _apiKey, preset) => {
              if (preset) setEntries((e) => ({ ...e, [server]: preset }));
              if (!picks.includes(server)) onPicksChange?.([...picks, server]);
              setAdding(false);
            }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="agent-mcp-add">
            {t('myBridge.mcp.add')}
          </Button>
        )}
      </div>
    </Frame>
  );
};

/** An existing agent: every server + the library (`enabled=false` = switched off: list + Remove only). */
const AgentServers: React.FC<{ agentId: string; enabled: boolean; publicAgent: boolean; classes: Classes }> = ({ agentId, enabled, publicAgent, classes }) => {
  const { t } = useTranslation(namespace);
  const { data: servers = [] } = backendApi.useGetMyBridgeAgentMcpServersQuery({ agentId });
  const [adding, setAdding] = useState(false);
  const connect = useMcpConnect(agentId, () => setAdding(false));

  if (!enabled && servers.length === 0) return null;
  return (
    <Frame classes={classes}>
      <div className="space-y-3" data-testid="agent-mcp-servers">
        <p className={classes.hint}>{t('myBridge.mcp.hint')}</p>
        {publicAgent && servers.some((s) => s.enabledTools.length > 0) && (
          <div className="flex items-start gap-2 rounded-ai-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300" data-testid="agent-mcp-public-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('myBridge.mcp.publicWarning')}</span>
          </div>
        )}
        {servers.map((s) => (
          <ServerCard key={s.id} agentId={agentId} server={s} enabled={enabled} classes={classes} connect={connect} />
        ))}
        {enabled &&
          (adding ? (
            <LibraryPicker
              classes={classes}
              busy={connect.busy}
              withKeys
              isAdded={(p) => servers.some((s) => s.preset === p.id || s.url === p.url)}
              directoryHref={`${MCP_DIRECTORY_PATH}?agent=${encodeURIComponent(agentId)}`}
              onPick={(server, apiKey) => void connect.addServer(server, apiKey)}
              onCancel={() => setAdding(false)}
            />
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="agent-mcp-add">
              {t('myBridge.mcp.add')}
            </Button>
          ))}
        {connect.error && (
          <p className="text-sm text-red-500" data-testid="agent-mcp-error">
            {connect.error}
          </p>
        )}
      </div>
    </Frame>
  );
};

/**
 * The builder chat's card: only the servers that still need the user — not connected,
 * or connected with no tool ticked (e.g. the ones the builder added from the prompt).
 * Renders nothing when there are none.
 */
export const McpConnectCard: React.FC<{ agentId: string }> = ({ agentId }) => {
  const { t } = useTranslation(namespace);
  const enabled = useMcpEnabled();
  const { data: servers = [] } = backendApi.useGetMyBridgeAgentMcpServersQuery({ agentId }, { skip: !enabled });
  const connect = useMcpConnect(agentId);
  const attention = servers.filter((s) => !s.connected || !!s.lastError || s.enabledTools.length === 0);
  if (!enabled || attention.length === 0) return null;
  const classes: Classes = { section: '', title: '', label: 'text-sm font-medium text-ai-text', hint: 'text-xs text-ai-text-secondary', input: '' };
  return (
    <div className="mt-2 space-y-2 rounded-ai-md border border-ai-border bg-ai-surface p-3" data-testid="agent-mcp-connect-card">
      <div className="text-sm font-medium text-ai-text">{t('myBridge.mcp.cardTitle')}</div>
      {attention.map((s) => (
        <ServerCard key={s.id} agentId={agentId} server={s} enabled classes={classes} connect={connect} />
      ))}
      {connect.error && <p className="text-sm text-red-500">{connect.error}</p>}
    </div>
  );
};
